import { observable } from 'mobx';
import _ from 'lodash';
import type { Moment } from 'moment';
import moment from 'moment';
import type { TransportElementTerminalResponseArgs } from './transport_element_terminal2';
import TransportElementTerminal, {
  convertTransportElementTerminalResponseToArgs
} from './transport_element_terminal2';
import { toDomesticTel } from '../util';
import type OrderItem from './order_item2';

export type TransportElementResponseArgs = {
  id: number;
  name: string;
  transport_type: string;
  from: TransportElementTerminalResponseArgs;
  to: TransportElementTerminalResponseArgs;
  air_reservation_number?: string;
  air_confirmation_number?: string;
  evo_reservation_number?: string;
  evo_confirmation_number?: string;
  evo_application_number?: string;
  evo_sky_pdf?: string;
  car_provider_text?: string;
  car_type_text?: string;
  last_name_kana?: string;
  first_name_kana?: string;
  driver_tel?: string;
  remarks?: string;
  ticketing_expired_at?: string;
  air_ticket_type_str?: string;
  air_seat_type_str?: string;
  boarding_station?: string;
  arrival_station?: string;
  air_ticket_type?: number;
  marketlog_available?: boolean;
};

export type TransportElementArgs = {
  id: number;
  name: string;
  transportType: string;
  from: TransportElementTerminal;
  to: TransportElementTerminal;
  airReservationNumber?: string;
  airConfirmationNumber?: string;
  evoReservationNumber?: string;
  evoConfirmationNumber?: string;
  evoApplicationNumber?: string;
  evoSkyPdf?: string;
  carProviderText?: string;
  carTypeText?: string;
  lastNameKana?: string;
  firstNameKana?: string;
  driverTel?: string;
  remarks?: string;
  ticketingExpiredAt?: Moment;
  airTicketTypeStr?: string;
  airSeatTypeStr?: string;
  boardingStation?: string;
  arrivalStation?: string;
  airTicketType?: number;
  marketlogAvailable?: boolean;
};

export const convertTransportElementResponseToArgs = (response: TransportElementResponseArgs) => {
  const args: TransportElementArgs = {
    id: response.id,
    name: response.name,
    transportType: response.transport_type,
    from: new TransportElementTerminal(convertTransportElementTerminalResponseToArgs(response.from)),
    to: new TransportElementTerminal(convertTransportElementTerminalResponseToArgs(response.to)),
    airReservationNumber: response.air_reservation_number,
    airConfirmationNumber: response.air_confirmation_number,
    evoReservationNumber: response.evo_reservation_number,
    evoConfirmationNumber: response.evo_confirmation_number,
    evoApplicationNumber: response.evo_application_number,
    evoSkyPdf: response.evo_sky_pdf,
    carProviderText: response.car_provider_text,
    carTypeText: response.car_type_text,
    lastNameKana: response.last_name_kana,
    firstNameKana: response.first_name_kana,
    driverTel: response.driver_tel,
    remarks: response.remarks,
    ticketingExpiredAt: response.ticketing_expired_at ? moment(response.ticketing_expired_at) : undefined,
    airTicketTypeStr: response.air_ticket_type_str,
    airSeatTypeStr: response.air_seat_type_str,
    boardingStation: response.boarding_station,
    arrivalStation: response.arrival_station,
    airTicketType: response.air_ticket_type,
    marketlogAvailable: response.marketlog_available
  };
  return args;
};

export default class TransportElement {
  @observable
  id: number;

  @observable
  name: string;

  @observable
  transportType: string;

  @observable
  from: TransportElementTerminal;

  @observable
  to: TransportElementTerminal;

  @observable
  airReservationNumber?: string;

  @observable
  airConfirmationNumber?: string;

  @observable
  evoReservationNumber?: string;

  @observable
  evoConfirmationNumber?: string;

  @observable
  evoApplicationNumber?: string;

  @observable
  evoSkyPdf?: string;

  @observable
  carProviderText?: string;

  @observable
  carTypeText?: string;

  @observable
  lastNameKana?: string;

  @observable
  firstNameKana?: string;

  @observable
  driverTel?: string;

  @observable
  remarks?: string;

  @observable
  ticketingExpiredAt?: Moment;

  @observable
  airTicketTypeStr?: string;

  @observable
  airSeatTypeStr?: string;

  @observable
  boardingStation?: string;

  @observable
  arrivalStation?: string;

  @observable
  airTicketType?: number;

  @observable
  marketlogAvailable?: boolean;

  constructor(args: TransportElementArgs) {
    this.id = args.id;
    this.name = args.name;
    this.transportType = args.transportType;
    this.from = args.from;
    this.to = args.to;
    this.airReservationNumber = args.airReservationNumber;
    this.airConfirmationNumber = args.airConfirmationNumber;
    this.evoReservationNumber = args.evoReservationNumber;
    this.evoConfirmationNumber = args.evoConfirmationNumber;
    this.evoApplicationNumber = args.evoApplicationNumber;
    this.evoSkyPdf = args.evoSkyPdf;
    this.carProviderText = args.carProviderText;
    this.carTypeText = args.carTypeText;
    this.lastNameKana = args.lastNameKana;
    this.firstNameKana = args.firstNameKana;
    this.driverTel = args.driverTel;
    this.remarks = args.remarks;
    this.ticketingExpiredAt = args.ticketingExpiredAt;
    this.airTicketTypeStr = args.airTicketTypeStr;
    this.airSeatTypeStr = args.airSeatTypeStr;
    this.boardingStation = args.boardingStation;
    this.arrivalStation = args.arrivalStation;
    this.airTicketType = args.airTicketType;
    this.marketlogAvailable = args.marketlogAvailable;
  }

  get type() {
    return 'transport';
  }

  get summary() {
    let s = '';
    if (this.transportType === 'foreign_air') {
      s = `${this.from.foreignAirDescription}発 → ${this.to.foreignAirDescription}着 ${this.name}`;
    } else {
      s = `${this.from.description}発 → ${this.to.description}着`;
      if (this.name) {
        s += ` ${this.name.replace(/\n/g, ' ')}`;
      }
    }
    if (!this.isEvoNumbers && this.airReservationNumber && this.airConfirmationNumber) {
      s += `\n予約番号:${this.airReservationNumber}`;
      s += `\n確認番号:${this.airConfirmationNumber}`;
    }
    if (this.evoReservationNumber) {
      s += `\n予約番号:${this.evoReservationNumber}`;
    }
    if (this.evoConfirmationNumber) {
      s += `\n確認番号:${this.evoConfirmationNumber}`;
    }
    if (this.evoApplicationNumber) {
      s += `\n問い合わせ番号:${this.evoApplicationNumber}`;
    }
    if (this.evoSkyPdf) {
      s += `\nSKYPDF:${this.evoSkyPdf}`;
    }
    if (this.carProviderText) {
      s += `\n提供元:${this.carProviderText}`;
    }
    if (this.carTypeText) {
      s += `\n車種・クラス:${this.carTypeText}`;
    }
    if (this.lastNameKana && this.firstNameKana) {
      s += `\n運転者氏名（カナ）:${this.lastNameKana} ${this.firstNameKana}`;
    }
    if (this.driverTel) {
      s += `\n運転者電話番号:${toDomesticTel(this.driverTel)}`;
    }
    if (this.remarks) {
      s += `\n備考:${this.remarks}`;
    }
    return s;
  }

  get isEvoNumbers() {
    return !_.isEmpty(this.evoReservationNumber || this.evoConfirmationNumber || this.evoApplicationNumber);
  }

  get title() {
    switch (this.transportType) {
      case 'domestic_air':
        return '国内航空券';
      case 'foreign_air':
        return '海外航空券';
      case 'shinkansen':
        return '新幹線';
      case 'express':
        return '特急';
      case 'railway_ticket':
        return '在来線';
      case 'foreign_railway':
        return '海外鉄道';
      case 'rental_car':
        return 'レンタカー';
      case 'bus':
        return 'バス';
      case 'ana_tabisaku':
        return 'ANA旅作';
      case 'taxi':
        return 'タクシー';
      case 'other':
        return 'その他';
      default:
        return '';
    }
  }

  get isRailway() {
    return (
      this.transportType === 'shinkansen' ||
      this.transportType === 'express' ||
      this.transportType === 'railway_ticket' ||
      this.transportType === 'foreign_railway'
    );
  }

  get isAir() {
    return this.transportType === 'domestic_air' || this.transportType === 'foreign_air';
  }

  get isDomesticAir() {
    return this.transportType === 'domestic_air';
  }

  get isForeignAir() {
    return this.transportType === 'foreign_air';
  }

  get isPackage() {
    return [2, 3].includes(this.airTicketType || 0);
  }

  get marketLogType() {
    if (this.transportType === 'foreign_air') return this.type;
    return this.transportType;
  }

  detail(orderItem: OrderItem) {
    return this.structuredDetail(orderItem)
      .map(line => `${line.label}${line.value}\n`)
      .join('');
  }

  structuredDetail(orderItem: OrderItem) {
    const lines = [];
    if (this.ticketingExpiredAt) {
      lines.push({
        label: '',
        value: `発券期限:${this.ticketingExpiredAt.format('YYYY年MM月DD日HH時mm分')}`,
        translate: true
      });
    }
    if (!this.isRailway) {
      lines.push({
        label: '',
        value: '※表記は現地時刻です。',
        translate: true
      });
    }
    const seatType = this.travelerSeatTypeStr(orderItem);
    if (seatType) {
      lines.push({
        label: '',
        value: seatType,
        translate: true
      });
    }

    const ticketType = this.travelerTicketTypeStr(orderItem);
    if (ticketType) {
      lines.push({
        label: '',
        value: ticketType,
        translate: true
      });
    }

    const airTicketType = this.airTicketTypeStr;
    if (airTicketType) {
      lines.push({
        label: '',
        value: airTicketType,
        translate: true
      });
    }

    if (this.airSeatTypeStr) {
      lines.push({
        label: '',
        value: this.airSeatTypeStr,
        translate: true
      });
    }

    if (this.isRailway) {
      if (this.boardingStation) {
        lines.push({
          label: '乗車駅:',
          value: this.boardingStation,
          translate: false
        });
      }
      if (this.arrivalStation) {
        lines.push({
          label: '降車駅:',
          value: this.arrivalStation,
          translate: false
        });
      }
    }

    if (orderItem.isJrPackage) {
      if (orderItem.jrAuthCode) {
        lines.push({
          label: 'JR認証番号:',
          value: orderItem.jrAuthCode,
          translate: false
        });
      }
      if (orderItem.jrPickupCode) {
        lines.push({
          label: 'JR引取番号:',
          value: orderItem.jrPickupCode,
          translate: false
        });
      }
    }

    return lines;
  }

  travelerSeatTypeStr(orderItem: OrderItem) {
    if (!this.isRailway) {
      return '';
    }
    return orderItem.travelerShinkansenSeatTypes;
  }

  travelerTicketTypeStr(orderItem: OrderItem) {
    if (!this.isRailway) {
      return '';
    }
    return orderItem.travelerShinkansenTicketTypes;
  }
}
